// src/components/billing/BillingAlerts.js
import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Alert,
    AlertTitle,
    Chip,
    Stack,
    Divider,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    CircularProgress
} from '@mui/material';
import {
    Warning as WarningIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    Announcement as AnnouncementIcon,
    Event as CalendarIcon,
    Receipt as ReceiptIcon,
    PaymentRounded as PaymentIcon,
    QueryBuilder as ClockIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { post } from '../../services/apiService';

/**
 * Componente que muestra alertas y resumen del ciclo de facturación
 * @param {Object} billingCycle - Datos del ciclo de facturación
 * @param {Object} lastInvoice - Datos de la última factura
 * @param {string} clientStatus - Estado del cliente ('active', 'inactive', etc.)
 * @param {Function} onRefresh - Callback para actualizar los datos tras registrar prórroga
 */
const BillingAlerts = ({ billingCycle, lastInvoice, clientStatus, onRefresh }) => {
    const [openDialog, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [showPromiseAlert, setShowPromiseAlert] = useState(true);

    // No mostrar nada si no hay datos
    if (!billingCycle) {
        return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Fechas importantes
    const paymentDate = billingCycle.paymentDate ? new Date(billingCycle.paymentDate) : null;
    const cutoffDate = billingCycle.cutoffDate ? new Date(billingCycle.cutoffDate) : null;
    const billingDate = billingCycle.billingDate ? new Date(billingCycle.billingDate) : null;

    // Calcular días hasta fechas importantes
    const daysUntilPayment = paymentDate ?
        Math.ceil((paymentDate - today) / (1000 * 60 * 60 * 24)) : null;

    const daysUntilCutoff = cutoffDate ?
        Math.ceil((cutoffDate - today) / (1000 * 60 * 60 * 24)) : null;

    // Estado del servicio y factura
    const isServiceActive = clientStatus === 'active';

    // Verificamos si la factura está pagada
    const isPaid = lastInvoice?.status === 'paid';

    // Cualquier factura no pagada se considera pendiente
    const isPending = lastInvoice && !isPaid;

    // Verificar si la factura está vencida por fecha o estado explícito
    const isOverdue = paymentDate && today > paymentDate;
    const isExplicitlyOverdue = lastInvoice?.status === 'overdue' || lastInvoice?.status === 'vencida';
    const isAnyOverdue = isOverdue || isExplicitlyOverdue;

    // Formatear fecha para mostrar
    const formatDate = (date) => {
        if (!date) return 'No disponible';
        const d = new Date(date);
        return d.toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const handleRequestPromise = async () => {
        if (!lastInvoice) return;
        setLoading(true);
        setErrorMsg('');
        setSuccessMsg('');
        try {
            const res = await post(`/client/invoices/${lastInvoice.id}/promise`, {}, true);
            if (res.success) {
                setSuccessMsg('Tu prórroga ha sido activada con éxito. El servicio de internet ha sido restablecido.');
                setTimeout(() => {
                    setOpenDialog(false);
                    if (onRefresh && typeof onRefresh === 'function') {
                        onRefresh();
                    }
                }, 3000);
            } else {
                setErrorMsg(res.message || 'Error al procesar la prórroga.');
            }
        } catch (err) {
            console.error('Error requesting promise:', err);
            setErrorMsg(err.message || 'Error de conexión al servidor al solicitar prórroga.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ mb: 4 }}>
            <Stack spacing={2}>
                {/* Alerta de servicio suspendido con opción de Prórroga auto-gestionada */}
                {!isServiceActive && (
                    <Alert
                        severity="error"
                        variant="filled"
                        icon={<ErrorIcon />}
                        action={
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: { xs: 1, sm: 0 } }}>
                                {lastInvoice && !lastInvoice.payment_promise_date && (
                                    <Button
                                        color="warning"
                                        variant="contained"
                                        size="small"
                                        onClick={() => setOpenDialog(true)}
                                        startIcon={<ClockIcon />}
                                        sx={{ 
                                            bgcolor: 'warning.main', 
                                            color: 'warning.contrastText',
                                            fontWeight: 'bold',
                                            '&:hover': { bgcolor: 'warning.dark' }
                                        }}
                                    >
                                        SOLICITAR PRÓRROGA
                                    </Button>
                                )}
                                {lastInvoice && (
                                    <Button
                                        color="inherit"
                                        size="small"
                                        component={Link}
                                        to="/client/payments"
                                        state={{ invoiceId: lastInvoice.id, autoOpenPayment: true }}
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        PAGAR AHORA
                                    </Button>
                                )}
                            </Stack>
                        }
                    >
                        <AlertTitle sx={{ fontWeight: 'bold' }}>Servicio Suspendido</AlertTitle>
                        Su servicio ha sido suspendido por falta de pago. Realice un pago para reactivarlo inmediatamente.
                        {lastInvoice && !lastInvoice.payment_promise_date && (
                            <Box sx={{ mt: 1, fontSize: '0.85rem', opacity: 0.95 }}>
                                <strong>¿No puede pagar hoy?</strong> Puede solicitar una prórroga auto-gestionada de 7 días para reactivar su internet hoy mismo.
                            </Box>
                        )}
                    </Alert>
                )}

                {/* Alerta de Prórroga Activa */}
                {showPromiseAlert && lastInvoice?.payment_promise_date && new Date(lastInvoice.payment_promise_date) >= today && (
                    <Alert
                        severity="warning"
                        icon={<InfoIcon />}
                        onClose={() => setShowPromiseAlert(false)}
                    >
                        <AlertTitle sx={{ fontWeight: 'bold' }}>Prórroga de Servicio Activa</AlertTitle>
                        Tienes una prórroga activa hasta el <strong>{formatDate(lastInvoice.payment_promise_date)}</strong>. Por favor, asegúrate de realizar tu pago antes de esta fecha para evitar la suspensión definitiva de tu servicio.
                    </Alert>
                )}

                {/* Alerta de fecha de corte próxima (solo si está activo pero tiene facturas pendientes y no tiene prórroga activa) */}
                {isServiceActive && isPending && (!lastInvoice.payment_promise_date || new Date(lastInvoice.payment_promise_date) < today) && daysUntilCutoff !== null && daysUntilCutoff <= 3 && daysUntilCutoff > 0 && (
                    <Alert
                        severity="warning"
                        variant="filled"
                        icon={<WarningIcon />}
                        action={
                            lastInvoice && (
                                <Button
                                    color="inherit"
                                    size="small"
                                    component={Link}
                                    to="/client/payments"
                                    state={{ invoiceId: lastInvoice.id, autoOpenPayment: true }}
                                >
                                    PAGAR AHORA
                                </Button>
                            )
                        }
                    >
                        <AlertTitle>Suspensión Inminente</AlertTitle>
                        {daysUntilCutoff === 1 ? (
                            'Su servicio será suspendido MAÑANA si no realiza el pago pendiente.'
                        ) : (
                            `Su servicio será suspendido en ${daysUntilCutoff} días si no realiza el pago pendiente.`
                        )}
                    </Alert>
                )}

                {/* Alerta de fecha de pago vencida (solo si activo pero vencido y no tiene prórroga activa) */}
                {isServiceActive && isPending && (!lastInvoice.payment_promise_date || new Date(lastInvoice.payment_promise_date) < today) && isAnyOverdue && (!daysUntilCutoff || daysUntilCutoff > 3) && (
                    <Alert
                        severity="warning"
                        icon={<WarningIcon />}
                        action={
                            lastInvoice && (
                                <Button
                                    color="inherit"
                                    size="small"
                                    component={Link}
                                    to="/client/payments"
                                    state={{ invoiceId: lastInvoice.id }}
                                >
                                    PAGAR AHORA
                                </Button>
                            )
                        }
                    >
                        <AlertTitle>Pago Vencido</AlertTitle>
                        La fecha límite de pago ha vencido. Su servicio será suspendido el {formatDate(cutoffDate)}.
                    </Alert>
                )}

                {/* Alerta de fecha de pago próxima */}
                {isServiceActive && isPending && !isAnyOverdue && daysUntilPayment !== null && daysUntilPayment <= 3 && daysUntilPayment > 0 && (
                    <Alert
                        severity="info"
                        icon={<InfoIcon />}
                        action={
                            lastInvoice && (
                                <Button
                                    color="inherit"
                                    size="small"
                                    component={Link}
                                    to="/client/payments"
                                    state={{ invoiceId: lastInvoice.id }}
                                >
                                    PAGAR AHORA
                                </Button>
                            )
                        }
                    >
                        <AlertTitle>Pago Próximo a Vencer</AlertTitle>
                        {daysUntilPayment === 1 ? (
                            'Su fecha límite de pago vence MAÑANA.'
                        ) : (
                            `Su fecha límite de pago vence en ${daysUntilPayment} días.`
                        )}
                    </Alert>
                )}

                {/* Recordatorio general de recibo pendiente */}
                {isServiceActive && isPending && !isAnyOverdue && (!lastInvoice.payment_promise_date || new Date(lastInvoice.payment_promise_date) < today) && (daysUntilPayment === null || daysUntilPayment > 3) && (
                    <Alert
                        severity="info"
                        icon={<ReceiptIcon />}
                        action={
                            lastInvoice && (
                                <Button
                                    color="inherit"
                                    size="small"
                                    component={Link}
                                    to={`/client/invoices/${lastInvoice.id}`}
                                >
                                    VER DETALLE
                                </Button>
                            )
                        }
                    >
                        <AlertTitle>Recibo Pendiente</AlertTitle>
                        Tiene un recibo pendiente de pago por S/ {parseFloat(lastInvoice.amount || 0).toFixed(2)}. Fecha límite: {formatDate(lastInvoice.due_date || lastInvoice.payment_due_date || paymentDate)}.
                    </Alert>
                )}

                {/* Estado normal - todo pagado */}
                {isServiceActive && !isPending && (
                    <Alert
                        severity="success"
                        icon={<PaymentIcon />}
                    >
                        <AlertTitle>Servicio al Día</AlertTitle>
                        Su servicio está activo y sus pagos al día. ¡Gracias por su puntualidad!
                    </Alert>
                )}
            </Stack>

            {/* Dialog de Confirmación de Prórroga */}
            <Dialog
                open={openDialog}
                onClose={() => !loading && setOpenDialog(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ClockIcon color="warning" />
                    <span>Solicitar Prórroga de Servicio</span>
                </DialogTitle>
                <DialogContent>
                    {errorMsg && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {errorMsg}
                        </Alert>
                    )}
                    {successMsg && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {successMsg}
                        </Alert>
                    )}
                    
                    {!successMsg && (
                        <DialogContentText>
                            Al solicitar la prórroga, su servicio de internet se reactivará temporalmente por un plazo estricto de <strong>7 días</strong> a partir de hoy.
                            <br /><br />
                            <strong>Condiciones:</strong>
                            <br />
                            • Se otorga con el compromiso de que realizará el pago pendiente durante estos 7 días.
                            <br />
                            • Esta opción solo se puede solicitar <strong>una vez</strong> por factura pendiente.
                            <br /><br />
                            ¿Desea confirmar la solicitud de prórroga y reactivar su servicio ahora?
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions sx={{ pb: 2, px: 3 }}>
                    <Button onClick={() => setOpenDialog(false)} disabled={loading || !!successMsg}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleRequestPromise}
                        color="warning"
                        variant="contained"
                        disabled={loading || !!successMsg}
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {loading ? 'Procesando...' : 'Sí, Reactivar Servicio'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BillingAlerts;